import React, { useEffect, useMemo } from 'react';
import { Alert, Box, LinearProgress } from '@mui/material';
import { RESIZE_DEBOUNCE_MS } from '~/common/constants/animation-constants';
import { useOrgParams } from '~/common/use-org-params';
import { ImageOverlay, MapContainer, useMap } from 'react-leaflet';
import { CRS } from 'leaflet';
import { PlotPolygonsLayer } from './plot-polygons-layer';
import { ReferencePolygonLayer } from './reference-polygon-layer';
import 'leaflet/dist/leaflet.css';
import { useFarmClimate } from './use-farm-climate';
import { useMapImage } from './use-map-image';
import { MapZoomController } from './map-zoom-controller';

const HIDE_PLOTS_ON_ZOOM = false

const MapResizer = ({ width, height }) => {
  const map = useMap()

  // Explicit size prop change (e.g. dashboard card resize)
  useEffect(() => {
    if (!map) return
    map.invalidateSize()
  }, [map, width, height])

  // Container resize — debounced so sidebar/layout CSS transitions don't thrash Leaflet
  useEffect(() => {
    if (!map) return
    let timeout
    const observer = new ResizeObserver(() => {
      clearTimeout(timeout)
      timeout = setTimeout(() => map.invalidateSize(), RESIZE_DEBOUNCE_MS)
    })
    observer.observe(map.getContainer())
    return () => {
      observer.disconnect()
      clearTimeout(timeout)
    }
  }, [map])

  return null
}

export const StaticMap = ({
  children,
  width,
  height,
  fullImage = false,
  showPlotOrganizationPolygons = false,
  disableAutoZoom = false,
  onPlotHover,
  onPlotClick,
  onMapClick,
  animRef,
  orgZoom = true,
  editingPlot,
  showPolygonLabels = false,
  selectedPlotId,
  clampToBounds = false,
  ...props
}) => {
  const { farmId } = useOrgParams()
  const { climate } = useFarmClimate()
  const { mapUrl, imageDimensions, error: mapError } = useMapImage(climate)

  // Calculate bounds and center for CRS.Simple coordinate system.
  // Memoized so the array reference stays stable across re-renders when dimensions
  // haven't changed — prevents MapZoomController's effect from re-firing (and calling
  // flyToBounds on a 0×0 container) whenever an unrelated prop like width/height changes.
  const imageBounds = useMemo(
    () => imageDimensions
      ? [[0, 0], [imageDimensions.height, imageDimensions.width]]
      : [[0, 0], [2000, 2000]],
    [imageDimensions]
  )

  const center = imageDimensions
    ? [imageDimensions.height / 2, imageDimensions.width / 2]
    : [1000, 1000]

  // Calculate optimal zoom to fill container
  // For CRS.Simple: screenPixels = imagePixels * 2^zoom
  const initialZoom = useMemo(() => {
    if (!width || !height || !imageDimensions) return -1

    const zoomForWidth = Math.log2(width / imageDimensions.width)
    const zoomForHeight = Math.log2(height / imageDimensions.height)

    // Use minimum to ensure image fits without cropping
    const optimalZoom = Math.min(zoomForWidth, zoomForHeight)

    // Clamp to zoom bounds
    return Math.max(-3, Math.min(2, optimalZoom))
  }, [width, height, imageDimensions])

  if (!farmId) {
    return <LinearProgress />
  }

  return (
    <Box
      sx={{
        position: 'relative',
        ...(width && height
          ? { width: `${width}px`, height: `${height}px` }
          : { width: '100%', height: '100%' }
        ),
        // Hide polygons during zoom, fade back in when done
        '& .leaflet-overlay-pane svg': {
          transition: 'opacity 0.35s ease',
        },
        ...(HIDE_PLOTS_ON_ZOOM && {
          '& .tp-map-zooming .leaflet-overlay-pane svg': {
            opacity: 0,
            transition: 'opacity 0.15s ease',
          },
        }),
        // Polygon fill labels
        '& .tp-plot-label': {
          background: 'rgba(0,0,0,0.55)',
          border: 'none',
          borderRadius: '4px',
          color: '#fff',
          fontSize: '0.65rem',
          fontWeight: 500,
          padding: '2px 5px',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        },
        '& .tp-plot-label::before': { display: 'none' },
        '& .tp-plot-label--editing': {
          background: 'rgba(237,108,2,0.85)',
        },
      }}
    >
      {mapError ? (
        <Alert severity="error" sx={{ width: '100%', height: '100%', boxSizing: 'border-box' }}>
          {mapError.message || 'An error occured loading the map, please refresh'}
        </Alert>
      ) : mapUrl && imageDimensions ? (
        <MapContainer
          crs={CRS.Simple}
          center={center}
          zoom={initialZoom}
          zoomSnap={0}
          zoomDelta={0.25}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 4,
            backgroundColor: 'transparent',
          }}
          scrollWheelZoom={true}
          doubleClickZoom={false}
          zoomControl={false}
          attributionControl={false}
          minZoom={-3}
          maxZoom={2}
          {...props}
          maxBounds={clampToBounds ? imageBounds : undefined}
          maxBoundsViscosity={clampToBounds ? 1.0 : undefined}
        >
          {/* 
            Include the 'tp-map-border-img' to apply custom styling
          */}
          <MapResizer width={width} height={height} />

          <ImageOverlay
            url={mapUrl}
            bounds={imageBounds}
            className="tp-map-border-img"
          />

          {/* Zoom controller for navigation and background clicks */}
          {showPlotOrganizationPolygons && farmId && (
            <MapZoomController
              farmId={farmId}
              fullImage={fullImage}
              imageBounds={imageBounds}
              onMapClick={onMapClick}
              disableAutoZoom={disableAutoZoom}
              animRef={animRef}
              orgZoom={orgZoom}
            />
          )}

          {/* Reference polygons layer (beneath plot polygons) */}
          {showPlotOrganizationPolygons && farmId && (
            <ReferencePolygonLayer farmId={farmId} />
          )}

          {/* Plot organization polygons layer (background) */}
          {showPlotOrganizationPolygons && farmId && (
            <PlotPolygonsLayer
              farmId={farmId}
              onPlotHover={onPlotHover}
              onPlotClick={onPlotClick}
              editingPlot={editingPlot}
              showPolygonLabels={showPolygonLabels}
              selectedPlotId={selectedPlotId}
            />
          )}

          {children}
        </MapContainer>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          {climate?.zoom != null ? <LinearProgress sx={{ width: '50%' }} /> : 'No map image available'}
        </Box>
      )}
    </Box>
  );
};
